import type { EnumValue } from '@repo/internal-tools-type-utils/src/utils.ts'
import type { AnalyticsAdapter } from './analyticsAdapter.ts'
import { ConsoleAnalyticsAdapter } from './ConsoleAnalyticsAdapter.ts'
import { ENV_ANALYTICS_ADAPTER } from './env.ts'
import { MockAnalyticsAdapter } from './MockAnalyticsAdapter.ts'
import { NoOpAnalyticsAdapter } from './NoOpAnalyticsAdapter.ts'
import { SegmentAnalyticsAdapter } from './SegmentAnalyticsAdapter.ts'

type AnalyticsAdapterConstructor = new () => AnalyticsAdapter

export const ANALYTICS_ADAPTER_REGISTRY = {
  console: ConsoleAnalyticsAdapter,
  segment: SegmentAnalyticsAdapter,
  'no-op': NoOpAnalyticsAdapter,
  mock: MockAnalyticsAdapter,
} as const satisfies Record<EnumValue, AnalyticsAdapterConstructor>

export type AnalyticsAdapterMode = keyof typeof ANALYTICS_ADAPTER_REGISTRY

const isAnalyticsAdapterMode = (
  value: unknown,
): value is AnalyticsAdapterMode =>
  typeof value === 'string' &&
  Object.prototype.hasOwnProperty.call(ANALYTICS_ADAPTER_REGISTRY, value)

const getAnalyticsAdapterModeFromEnv = (): AnalyticsAdapterMode => {
  const rawMode = ENV_ANALYTICS_ADAPTER

  if (isAnalyticsAdapterMode(rawMode)) {
    return rawMode
  }

  if (typeof rawMode === 'string' && rawMode.length > 0) {
    console.warn(
      `[createAnalyticsAdapterFromEnv]: Unknown VITE_ANALYTICS_ADAPTER="${rawMode}". Falling back to "console".`,
    )
  }

  // Default mode
  return 'console'
}

/**
 * Creates an analytics adapter using environment configuration.
 */
export const createAnalyticsAdapterFromEnv = (): AnalyticsAdapter => {
  const mode = getAnalyticsAdapterModeFromEnv()
  const Adapter = ANALYTICS_ADAPTER_REGISTRY[mode]
  return new Adapter()
}
